import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { Sparkle, FileText, ClipboardText, Copy, UploadSimple } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const BUREAUS = ["Equifax", "Experian", "TransUnion"];

export function AIAssistant({ clientId }) {
  const [busy, setBusy] = useState("");        // '', 'summary', 'dispute', 'analyze'
  const [result, setResult] = useState("");
  const [title, setTitle] = useState("");
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [dispute, setDispute] = useState({ bureau: "Equifax", account: "", reason: "", instructions: "" });
  const fileRef = useRef(null);

  const run = async (kind, fn, label) => {
    setBusy(kind); setTitle(label);
    try { const { data } = await fn(); setResult(data.text || ""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI request failed"); setResult(""); }
    finally { setBusy(""); }
  };

  const summarize = () => run("summary", () => api.post(`/cc/clients/${clientId}/ai/summary`), "Client Summary");
  const draftLetter = () => {
    setDisputeOpen(false);
    run("dispute", () => api.post(`/cc/clients/${clientId}/ai/dispute-letter`, dispute), "Dispute Letter Draft");
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    await run("analyze", () => api.post(`/cc/clients/${clientId}/ai/analyze-report`, fd, { headers: { "Content-Type": "multipart/form-data" } }), `Report Analysis · ${file.name}`);
    if (fileRef.current) fileRef.current.value = "";
  };
  const copy = () => { if (navigator.clipboard) navigator.clipboard.writeText(result); toast.success("Copied to clipboard"); };

  return (
    <div data-testid="ai-assistant-section">
      <div className="flex items-center gap-2 mb-4">
        <Sparkle size={20} weight="fill" className="text-primary" />
        <h2 className="text-lg font-medium">AI Assistant</h2>
        <span className="text-xs text-muted-foreground">· Claude</span>
      </div>

      <div className="rounded-xl border border-primary/20 bg-primary/5 p-5">
        <div className="flex flex-wrap gap-3">
          <Button onClick={summarize} disabled={!!busy} data-testid="ai-summarize-button" className="bg-primary text-primary-foreground hover:bg-[#DBC197] gap-2">
            <ClipboardText size={16} /> {busy === "summary" ? "Summarizing…" : "Summarize client"}
          </Button>
          <Button onClick={() => setDisputeOpen(true)} disabled={!!busy} variant="outline" data-testid="ai-dispute-button" className="border-white/15 gap-2">
            <FileText size={16} /> Draft dispute letter
          </Button>
          <Button onClick={() => fileRef.current?.click()} disabled={!!busy} variant="outline" data-testid="ai-analyze-button" className="border-white/15 gap-2">
            <UploadSimple size={16} /> {busy === "analyze" ? "Analyzing…" : "Analyze credit report"}
          </Button>
          <input ref={fileRef} type="file" accept=".pdf,.txt,text/plain,application/pdf" onChange={onFile} className="hidden" data-testid="ai-report-file-input" />
        </div>
        <p className="text-xs text-muted-foreground mt-3">Analyze accepts a text-based PDF or .txt export of the credit report (scanned images aren't supported).</p>

        {(busy || result) && (
          <div className="mt-5 rounded-lg border border-white/10 bg-zinc-950/60 p-4" data-testid="ai-result">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-primary">{title}</div>
              {result && <Button size="sm" variant="ghost" onClick={copy} data-testid="ai-copy-button" className="h-7 gap-1 text-xs"><Copy size={14} /> Copy</Button>}
            </div>
            {busy ? (
              <div className="text-sm text-muted-foreground animate-pulse">Thinking… this can take a few seconds.</div>
            ) : (
              <pre className="text-sm whitespace-pre-wrap font-sans leading-relaxed max-h-[520px] overflow-auto">{result}</pre>
            )}
          </div>
        )}
      </div>

      <Dialog open={disputeOpen} onOpenChange={setDisputeOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle>Draft Dispute Letter</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">The client's name & address are pulled from their profile automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Bureau</Label>
              <Select value={dispute.bureau} onValueChange={(v) => setDispute((d) => ({ ...d, bureau: v }))}>
                <SelectTrigger data-testid="ai-dispute-bureau"><SelectValue /></SelectTrigger>
                <SelectContent>{BUREAUS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Account / item to dispute</Label><Input data-testid="ai-dispute-account" value={dispute.account} onChange={(e) => setDispute((d) => ({ ...d, account: e.target.value }))} placeholder="e.g. Capital One #1234 collection" /></div>
            <div><Label>Reason</Label><Input data-testid="ai-dispute-reason" value={dispute.reason} onChange={(e) => setDispute((d) => ({ ...d, reason: e.target.value }))} placeholder="e.g. Not mine / inaccurate balance" /></div>
            <div><Label>Extra instructions</Label><Textarea data-testid="ai-dispute-instructions" value={dispute.instructions} onChange={(e) => setDispute((d) => ({ ...d, instructions: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDisputeOpen(false)}>Cancel</Button>
            <Button data-testid="ai-dispute-generate" onClick={draftLetter} className="bg-primary text-primary-foreground hover:bg-[#DBC197]">Generate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
